'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["errorKey", "status", "defaultValue", "context", "location", "widgetId", "criterionId", "devices"];
import I18n from 'I18n';
import { createSelector } from 'reselect';
import { fromJS } from 'immutable';
import { reduce, sortBy } from 'underscore';
import { getShouldPublishImmediately } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { makeDataValidationErrors } from 'ContentEditorUI/redux/selectors/moduleErrorSelectors';
import { convertObjectToCamelCase } from 'ContentEditorUI/utils/helpers';
import { basicSelector } from 'ContentEditorUI/redux/selectors/helpers';
import { UNVERIFIED_EMAILS_RESULT_TYPES } from 'EmailEditorUI/constants/ContactVerificationConstants';
import { getIsUngatedForRestrictedContentValidationError, getIsUngatedForDisplayHublTemplateSyntaxValidation } from 'EmailEditorUI/redux/selectors/AuthSelectors';
const HIDDEN_LINKS = ['OK', 'UNKNOWN', 'LINKCHECKER_ERROR', 'LINKCHECKER_IGNORED', 'LINKCHECKER_WORKING', 'CONTAINS_HUBSPOT_TRACKING_PARAMS'];
const LOW_PRIORITY_TAG_LOCATIONS = ['html', 'plain_text'];
const SUPPORTED_FIELD_ERRORS = ['MALFORMED_MERGE_TAGS', 'BAD_MERGE_TAG_DEFAULT', 'MISSING_MERGE_TAG_DEFAULT', 'HIDDEN_MERGE_TAG_DEFAULT', 'INVALID_CONTEXT_FOR_MERGE_TAG', 'REPLY_TO_FAILED_VALIDATION', 'PAGE_EXPIRY_DATE_PUBLISH_BEFORE_EXPIRY', 'REPLY_TO_FAILED_VALIDATION', 'REPLY_TO_MERGE_TAGS_FORBIDDEN', 'REPLY_TO_UNVERIFIED_MERGE_TAG_DEFAULT', 'REPLY_TO_UNVERIFIED_EMAIL', 'REPLY_TO_AMONG_RECIPIENTS', 'SLUG_CONFLICT', 'CAMPAIGN_NOT_ALLOWED', 'SUBSCRIPTION_TYPE_BUSINESS_UNIT_VALIDATION', 'LIST_DELETED', 'NOT_SET', 'DATE_TOO_FAR_IN_FUTURE', 'PAGE_EXPIRY_INVALID_URL', 'DATE_IN_THE_PAST'];
const MERGE_TAG_ERROR_TYPES = ['MERGE_TAG_DEFAULTS', 'UNDEFINED_MERGE_TAG', 'MALFORMED_MERGE_TAGS', 'REQUIRED_MERGE_TAG_MISSING', 'REQUIRED_MERGE_TAG_HIDDEN', 'INVALID_CONTEXT_FOR_MERGE_TAG'];
const MERGE_TAG_DEFAULT_STATUS = {
  REQUIRED_MERGE_TAG_MISSING: 'required_tag',
  REQUIRED_MERGE_TAG_HIDDEN: 'hidden_tag',
  MALFORMED_MERGE_TAGS: 'malformed_merge_tags',
  UNDEFINED_MERGE_TAG: 'undefined_tag',
  INVALID_CONTEXT_FOR_MERGE_TAG: 'invalid_context_for_merge_tag'
};
const EMAIL_RENDERED_SUBJECT_NOT_BLANK = 'RENDERED_SUBJECT_NOT_BLANK';
export const PORTAL_MIGRATION_CONFLICT = 'PORTAL_MIGRATION_CONFLICT';
const getEmailValidationResult = basicSelector(state => state.emailValidation);
const errorMapSelector = (errorFields, allowlist, errorLevel, groupByField = 'errorKey') => {
  return createSelector([getEmailValidationResult], result => {
    var _result$errorFields;
    return result === null || result === void 0 || (_result$errorFields = result[errorFields]) === null || _result$errorFields === void 0 ? void 0 : _result$errorFields.reduce((errs, error) => {
      if ((!allowlist || allowlist && allowlist.indexOf(error.errorKey) > -1) && (!errorLevel || error.errorLevel === errorLevel)) {
        const key = error[groupByField];
        errs[key] = errs[key] ? [...errs[key], error] : [error];
      }
      return errs;
    }, {});
  });
};
export const getFieldErrorMap = errorMapSelector('fieldErrors', SUPPORTED_FIELD_ERRORS, 'ERROR', 'field');
export const getFieldWarningMap = errorMapSelector('fieldErrors', SUPPORTED_FIELD_ERRORS, 'WARNING', 'field');
export const getErrorMap = errorMapSelector('errors');
const getFieldErrors = createSelector([getFieldErrorMap], fieldErrors => {
  return reduce(fieldErrors, (result, value, key) => {
    result[key.toLowerCase()] = value;
    return result;
  }, {});
});
export const getPersonalizedReplyToError = createSelector([getErrorMap], errorMap => {
  const error = errorMap['REPLY_TO_UNVERIFIED_EMAIL'];
  if (error) {
    const isLoading = error.some(err => err.resultType === UNVERIFIED_EMAILS_RESULT_TYPES.VALIDATION_TASK_ENQUEUED || err.resultType === UNVERIFIED_EMAILS_RESULT_TYPES.VALIDATION_TASK_RUNNING);
    const isAutomated = error.some(err => err.resultType === UNVERIFIED_EMAILS_RESULT_TYPES.AUTOMATED_UNCHECKED);
    const locations = error.map(err => err.location);
    let unverifiedReplyToCount = 0;
    let hasMore = false;
    const replyToError = error.find(err => err.location === 'REPLY_TO' && err.validatorType === 'PERSONALIZED_REPLY_TO');
    if (replyToError) {
      unverifiedReplyToCount = replyToError.unverifiedCount;
      hasMore = !!replyToError.hasMore;
    }
    return {
      isLoading,
      isAutomated,
      unverifiedReplyToCount,
      locations,
      hasMore
    };
  }
  return {};
});
export const getPersonalizedReplyToWarning = createSelector([getPersonalizedReplyToError], personalizedReplyToError => {
  const {
    isLoading,
    unverifiedReplyToCount,
    locations,
    hasMore
  } = personalizedReplyToError;
  if (isLoading || unverifiedReplyToCount > 0) {
    return {
      personalizedReplyToHasUnverifiedEmailsWarning: {
        isLoading,
        errorLevel: 'WARNING',
        unverifiedReplyToCount,
        locations,
        hasMore,
        target: 'inbox-settings'
      }
    };
  }
  return {};
});
export const getPersonalizedReplyToSuggestionForAutomatedEmail = createSelector([getPersonalizedReplyToError], personalizedReplyToError => {
  const {
    isLoading,
    isAutomated,
    unverifiedReplyToCount,
    locations,
    hasMore
  } = personalizedReplyToError;
  if (isAutomated && unverifiedReplyToCount === 0) {
    return {
      personalizedReplyToHasUnverifiedEmailsWarning: {
        isLoading,
        isAutomatedUnchecked: true,
        errorLevel: 'INFO',
        unverifiedReplyToCount,
        locations,
        hasMore,
        target: 'inbox-settings'
      }
    };
  }
  return {};
});
export const getInfoMap = errorMapSelector('info');
const getInfo = createSelector([getInfoMap], info => {
  return reduce(info, (result, value, key) => {
    result[key.toLowerCase()] = value;
    return result;
  }, {});
});
export const createErrorValidator = selector => createSelector([selector], result => {
  return {
    errorKey: result.errorKey,
    error: result.error || result.errorLevel === 'ERROR',
    warning: result.errorLevel === 'WARNING',
    values: result.values || []
  };
});
export const getMergeTagsContext = createSelector([getInfo], info => {
  if (info.merge_tag_context) {
    const mergeTagsDefault = info.merge_tag_context.find(infoValidation => {
      return infoValidation.validatorType === 'MERGE_TAG_DEFAULTS';
    });
    return mergeTagsDefault ? mergeTagsDefault.context : {};
  } else {
    return {};
  }
});
export const getMergeTagsContextFields = createSelector([getInfo], info => {
  if (info.merge_tag_context) {
    const mergeTagsDefault = info.merge_tag_context.find(infoValidation => {
      return infoValidation.validatorType === 'MERGE_TAG_DEFAULTS_FIELDS';
    });
    return mergeTagsDefault ? mergeTagsDefault.context : {};
  } else {
    return {};
  }
});
export const getHasCustomTag = createSelector([getInfo], ({
  merge_tag_context = []
}) => merge_tag_context.some(({
  context
}) => context && context.custom));
const MALFORMED_REGEX_CHECKER = /\{+\{*\s*[A-Za-z0-9._()',\s"]+\s*\}*/;
const formatTag = tag => {
  const newTag = tag.match(MALFORMED_REGEX_CHECKER);
  if (newTag && newTag[0]) {
    return newTag[0];
  }
  return tag;
};
export const getRecipientsListDeletedError = createSelector([getFieldErrorMap], errorMap => {
  const error = errorMap['MAILING_LISTS_INCLUDED'];
  if (error) {
    return {
      recipients_list_deleted: {
        errorLevel: 'ERROR'
      }
    };
  }
  return {};
});
const mergeTagValidationSelectorsByErrorLevel = (mergeTagErrorKey, errorLevel, saveAsMap) => {
  return createSelector([getErrorMap], errorMap => {
    const errors = errorMap[mergeTagErrorKey] ? errorMap[mergeTagErrorKey].filter(e => e.errorLevel === errorLevel) : [];
    if (!errors.length) {
      return {
        errorLevel,
        values: [],
        errorKey: mergeTagErrorKey
      };
    }
    const defaultStatus = MERGE_TAG_DEFAULT_STATUS[mergeTagErrorKey];
    const values = mergeTagErrorKey === 'MERGE_TAG_DEFAULTS' ? errors[0].tags.filter(t => t.status === 'NO_DEFAULT') : errors;
    const reducedTags = values.reduce((built, mergeTag) => {
      const {
          errorKey,
          status,
          defaultValue,
          context,
          location,
          widgetId,
          criterionId,
          devices
        } = mergeTag,
        otherData = _objectWithoutPropertiesLoose(mergeTag, _excluded);
      let {
        tagName
      } = mergeTag;
      // currently we only process contact owners correctly as "owner.value", so convert all contact owner tokens to this
      tagName = tagName.replace('contact.hubspot_owner_id', 'owner');
      if (errorKey === 'MALFORMED_MERGE_TAGS') {
        tagName = formatTag(tagName);
      }
      const _location = location.toLowerCase();
      if (!built[tagName]) {
        built[tagName] = {
          tagName,
          status: status ? status.toLowerCase() : defaultStatus,
          defaultValue,
          context: context || '',
          locations: [],
          widgetId,
          criterionId,
          devices
        };
      }
      if (LOW_PRIORITY_TAG_LOCATIONS.indexOf(_location) > -1) {
        built[tagName].locations.push(_location);
      } else {
        built[tagName].locations.unshift(_location);
      }
      built[tagName].locations.sort();
      if (errorKey === 'INVALID_CONTEXT_FOR_MERGE_TAG') {
        built[tagName].workflowDetails = otherData.workflowDetails;
      }
      return built;
    }, {});
    const sortedTags = saveAsMap ? reducedTags : Object.keys(reducedTags).map(k => reducedTags[k]).sort((t1, t2) => {
      if (t1.tagName < t2.tagName) {
        return -1;
      } else if (t2.tagName > t1.tagName) {
        return 1;
      }
      return 0;
    });
    return {
      errorLevel,
      values: sortedTags,
      errorKey: mergeTagErrorKey
    };
  });
};
export const mergeTagErrorValidationSelectors = MERGE_TAG_ERROR_TYPES.reduce((selectors, mergeTagType) => {
  selectors[mergeTagType] = createErrorValidator(mergeTagValidationSelectorsByErrorLevel(mergeTagType, 'ERROR'));
  return selectors;
}, {});
export const mergeTagWarningValidationSelectors = MERGE_TAG_ERROR_TYPES.reduce((selectors, mergeTagType) => {
  selectors[mergeTagType] = createErrorValidator(mergeTagValidationSelectorsByErrorLevel(mergeTagType, 'WARNING'));
  return selectors;
}, {});
export const getSensitiveMergeTagError = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['SENSITIVE_MERGE_TAG'];
  if (!error) {
    return {};
  }
  return {
    errorKey: 'SENSITIVE_MERGE_TAG',
    errorLevel: 'ERROR'
  };
}));
export const getSocialSharingValidationWarning = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const warning = errorMap['SOCIAL_SHARING_VALIDATION'];
  if (!warning) {
    return {};
  }
  return {
    errorKey: 'SOCIAL_SHARING_VALIDATION',
    errorLevel: 'WARNING'
  };
}));
export const getSubscriptionLinkHiddenError = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const warning = errorMap['SUBSCRIPTION_LINK_HIDDEN'];
  if (!warning) {
    return {};
  }
  return {
    errorKey: 'SUBSCRIPTION_LINK_HIDDEN',
    errorLevel: 'ERROR'
  };
}));
export const getPortalMigrationError = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap[PORTAL_MIGRATION_CONFLICT];
  if (!error) {
    return {};
  }
  return {
    errorKey: 'PORTAL_MIGRATION_CONFLICT',
    errorLevel: 'ERROR'
  };
}));
export const getInvalidModulesValidation = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const invalidModules = errorMap['MODULE_DATA_INVALID'];
  if (!invalidModules) {
    return {};
  }
  return {
    error: invalidModules.length > 0,
    values: invalidModules[0].errors,
    errorKey: 'MODULE_DATA_INVALID'
  };
}));
export const getDomainValidation = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const domainValidation = errorMap['DOMAIN_NOT_RESOLVING'];
  if (!domainValidation) {
    return {};
  }
  return {
    error: domainValidation.length > 0,
    errorKey: 'DOMAIN_NOT_RESOLVING'
  };
}));
export const getCustomModuleJavascriptValidation = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['CUSTOM_WIDGETS_CONTAIN_JAVASCRIPT'];
  if (!error) {
    return {};
  }
  return {
    values: error[0],
    errorKey: 'CUSTOM_WIDGETS_CONTAIN_JAVASCRIPT',
    errorLevel: 'WARNING'
  };
}));
const createLinkErrorSelector = linkKey => {
  const linkErrorSelector = createSelector([getErrorMap], errorMap => errorMap[linkKey]);
  return createErrorValidator(createSelector([linkErrorSelector], errors => {
    if (!errors) {
      return {
        errorKey: linkKey
      };
    }
    const {
      links,
      errorLevel
    } = errors[0];
    const urls = links.filter(url => HIDDEN_LINKS.indexOf(url.linkResolutionStatus) < 0);
    const urlsMap = urls.reduce((mapped, url) => {
      if (!mapped[url.link]) {
        mapped[url.link] = {
          url: url.link,
          status: url.linkResolutionStatus.toLowerCase(),
          locations: url.linkLocation ? [url.linkLocation.toLowerCase()] : []
        };
      } else if (url.linkLocation) {
        mapped[url.link].locations.push(url.linkLocation);
      }
      return mapped;
    }, {});
    const values = sortBy(urlsMap, (obj, key) => key);
    return {
      values,
      errorLevel,
      errorKey: linkKey
    };
  }));
};
export const getLinkValidation = createLinkErrorSelector('LINK_VALIDATION');
export const getLinksWithRedundantTrackingParams = createSelector(getErrorMap, errorMap => {
  const errors = errorMap['LINK_VALIDATION'];
  if (!errors) return [];
  return errors[0].links.filter(e => e.linkResolutionStatus === 'CONTAINS_HUBSPOT_TRACKING_PARAMS');
});
export const getCanSpamNotConfiguredValidation = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['CAN_SPAM_NOT_CONFIGURED'];
  if (!error) {
    return {};
  }
  return {
    values: error[0],
    errorLevel: error[0].errorLevel,
    errorKey: 'CAN_SPAM_NOT_CONFIGURED'
  };
}));
export const getHasSlugConflict = createErrorValidator(createSelector([getFieldErrors], errors => {
  return errors.slug ? errors.slug[0] : {};
}));
export const getVariantValidation = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['VARIANT_VALIDATION_FAILED'];
  if (!error) {
    return {};
  }
  return {
    values: error[0],
    errorLevel: error[0].errorLevel,
    errorKey: 'VARIANT_VALIDATION_FAILED'
  };
}));
export const getMissingSubscriptionLinkErrors = createSelector([getErrorMap], errorMap => {
  const error = errorMap['MISSING_SUBSCRIPTION_LINK_IN_SUBSCRIPTION_EMAIL'];
  if (!error) {
    return {};
  }
  const {
    missingSubscriptionLink
  } = error[0];
  const emailType = missingSubscriptionLink === 'subscription_confirmation_url' ? 'OPTIN_EMAIL' : 'RESUBSCRIBE_EMAIL';
  return {
    missing_subscription_link: {
      values: {
        missingSubscriptionLink,
        emailType
      },
      isPlaintext: error.length === 1 && error[0].location === 'PLAIN_TEXT',
      errorLevel: error[0].errorLevel,
      errorKey: error[0].errorKey
    }
  };
});
export const getMissingFooterErrors = createSelector([getErrorMap], errorMap => {
  const error = errorMap['MISSING_FOOTER'];
  if (!error) {
    return {};
  }
  return {
    missing_footer: {
      values: error[0],
      isPlaintext: error.length === 1 && error[0].location === 'PLAIN_TEXT',
      errorLevel: error[0].errorLevel,
      errorKey: error[0].errorKey
    }
  };
});
export const getSendCreditsValidationErrors = createSelector([getErrorMap, getShouldPublishImmediately], (errorMap, publishImmediately) => {
  const error = errorMap['PORTAL_REACHED_SEND_LIMIT'];
  if (!error) {
    return {};
  }
  const newLimitPeriodDate = I18n.moment.portalTz().add(1, 'month').startOf('month');
  return {
    sendCreditsLimitReached: {
      type: 'send_limit_reached',
      isScheduled: !publishImmediately,
      nextPeriodDate: newLimitPeriodDate,
      errorLevel: error[0].errorLevel,
      errorKey: error[0].errorKey
    }
  };
});
export const getContentRestrictedValidationError = createSelector([getErrorMap, getIsUngatedForRestrictedContentValidationError], (errorMap, isUngatedForRestrictedContentValidationError) => {
  const error = errorMap['CONTENT_RESTRICTED'];
  if (!error || !isUngatedForRestrictedContentValidationError) {
    return {};
  }
  return {
    content_restricted: {
      type: 'content_restricted',
      errorLevel: error[0].errorLevel,
      errorKey: error[0].errorKey
    }
  };
});
export const getContentRestrictedValidationWarning = createSelector([getErrorMap, getIsUngatedForRestrictedContentValidationError], (errorMap, isUngatedForRestrictedContentValidationError) => {
  const error = errorMap['CONTENT_RESTRICTED'];
  if (!error || isUngatedForRestrictedContentValidationError) {
    return {};
  }
  return {
    content_restricted: {
      type: 'content_restricted',
      errorLevel: error[0].errorLevel,
      errorKey: error[0].errorKey
    }
  };
});
const createFooterVisibilityValidator = (type, errorLevel, keyName) => {
  return createSelector([getErrorMap], errorMap => {
    const warning = errorMap[type];
    if (!warning || warning[0].errorLevel !== errorLevel) {
      return {};
    }
    if (type === 'MOBILE_FOOTER_VISIBILITY' && !!errorMap.FOOTER_VISIBILITY) {
      return {};
    }
    return {
      [keyName]: {
        type,
        widgetId: warning[0].widgetId,
        field: warning[0].field,
        errorLevel: warning[0].errorLevel,
        errorKey: warning[0].errorKey
      }
    };
  });
};
export const getEmailFooterVisibilityWarning = createFooterVisibilityValidator('FOOTER_VISIBILITY', 'WARNING', 'footerVisibilityWarning');
export const getEmailFooterVisibilityError = createFooterVisibilityValidator('FOOTER_VISIBILITY', 'ERROR', 'footerVisibilityError');
export const getEmailMobileFooterVisibilityWarning = createFooterVisibilityValidator('MOBILE_FOOTER_VISIBILITY', 'WARNING', 'mobileFooterVisibilityWarning');
export const getEmailMobileFooterVisibilityError = createFooterVisibilityValidator('MOBILE_FOOTER_VISIBILITY', 'ERROR', 'mobileFooterVisibilityError');
export const getInvalidJmteSyntax = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['JMTE_TEMPLATE_SYNTAX_VALIDATION'];
  if (!error) {
    return {};
  }
  return {
    values: error[0],
    errorLevel: error[0].errorLevel,
    errorKey: error[0].errorKey
  };
}));
export const getHublTemplateSyntaxValidation = createSelector([getErrorMap, getIsUngatedForDisplayHublTemplateSyntaxValidation], (errorMap, isUngatedForDisplayHublTemplateSyntaxValidation) => {
  const error = errorMap.HUBL_TEMPLATE_SYNTAX_VALIDATION;
  if (!error || !isUngatedForDisplayHublTemplateSyntaxValidation) {
    return {};
  }
  return {
    invalid_hubl_syntax: {
      errorKey: error[0].errorKey,
      errorLevel: error[0].errorLevel,
      errorMessage: error[0].message
    }
  };
});
export const getStylesWithinBodyValidation = createSelector([getErrorMap], errorMap => {
  const error = errorMap['STYLES_WITHIN_BODY_VALIDATION'];
  if (!error) {
    return {};
  }
  return {
    styles_within_body_validation: {
      errorLevel: error[0].errorLevel,
      moduleIds: error[0].moduleIds
    }
  };
});
export const getSvgImageWarnings = createSelector([getErrorMap], errorMap => {
  const error = errorMap['SVG_IMAGE_VALIDATION'];
  if (!error) {
    return {};
  }
  return {
    svgImageWarning: {
      errorLevel: 'WARNING'
    }
  };
});
export const getInvalidSubscriptionLink = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['INVALID_SUBSCRIPTION_LINK'];
  if (!error) {
    return {
      values: []
    };
  }
  const linksWithLocation = (error[0].links || []).map(link => ({
    link,
    location: error[0].location
  }));
  return {
    values: linksWithLocation,
    errorKey: error[0].errorKey,
    errorLevel: error[0].errorLevel
  };
}));
export const getCampaignNotAllowed = createErrorValidator(createSelector([getFieldErrors], errors => {
  return errors.campaign ? errors.campaign[0] : {};
}));
export const getCombinedEmailValidationResult = createSelector([getEmailValidationResult], result => [...result.errors, ...result.fieldErrors]);
export const getIsValidationInErrorState = createSelector(getEmailValidationResult, result => result.status === 'ERROR');
const getModuleInvalidValidationErrors = (level = 'ERROR') => createSelector(getErrorMap, errorMap => {
  const error = errorMap.MODULE_DATA_INVALID && errorMap.MODULE_DATA_INVALID[0];
  if (error && error.errorLevel === level && error.errors) {
    return fromJS(error.errors.map(convertObjectToCamelCase));
  }
  return [];
});
export const getDataValidationErrors = makeDataValidationErrors(getModuleInvalidValidationErrors('ERROR'));
export const getDataValidationWarnings = makeDataValidationErrors(getModuleInvalidValidationErrors('WARNING'));
export const getInvalidEmailSubjectError = createSelector(getFieldErrorMap, fieldErrorMap => {
  if (fieldErrorMap.SUBJECT) {
    const error = fieldErrorMap.SUBJECT.find(({
      validatorType
    }) => validatorType === EMAIL_RENDERED_SUBJECT_NOT_BLANK);
    if (error) {
      return {
        rendered_subject_not_blank: error
      };
    }
  }
  return {};
});
export const getCustomModulesAccessError = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['CUSTOM_WIDGETS_ACCESS'];
  if (!error) {
    return {};
  }
  return {
    values: error[0],
    errorLevel: error[0].errorLevel,
    errorKey: error[0].errorKey
  };
}));
export const getAutoTranslationValidation = createErrorValidator(createSelector([getErrorMap], errorMap => {
  const error = errorMap['AUTO_TRANSLATED_EMAIL'];
  if (!error) {
    return {};
  }
  return {
    errorLevel: error[0].errorLevel,
    errorKey: error[0].errorKey
  };
}));
export const getHublLimitValidationError = (level = 'ERROR') => createSelector([getErrorMap], errorMap => {
  const error = errorMap['HUBL_FUNCTION_LIMITS_VALIDATION'];
  if (error && error[0].errorLevel === level) {
    return {
      hubl_limit_email: {
        errorLevel: error[0].errorLevel,
        errorKey: error[0].errorKey,
        functionName: error[0].functionName
      }
    };
  }
  return {};
});
export const getHublLimitValidationAsError = getHublLimitValidationError('ERROR');
export const getHublLimitValidationAsWarning = getHublLimitValidationError('WARNING');
export const getRequiredCustomPropertiesValidation = createSelector([getErrorMap], errorMap => {
  const error = errorMap['CUSTOM_PROP_REQUIRED_PROPERTY_MISSING'];
  if (!error) {
    return {};
  }
  return {
    custom_prop_required_property_missing: {
      type: 'custom_prop_required_property_missing',
      errorLevel: error[0].errorLevel,
      errorKey: error[0].errorKey,
      properties: error.map(e => e.customPropertyName)
    }
  };
});